/**
 * InboxPage — Unified Inbox Packet 1 + Packet 2
 *
 * Single sortable data table surfacing all three open kinds:
 *   qc (work items needing QC), decision (pending approval), cascade (verification inbox).
 *
 * AC-inbox-1:  one table, all three kinds
 * AC-inbox-2:  columns: Kind, Title/Summary, State, Age, Owner/Domain, Actions
 * AC-inbox-3:  default sort = oldest-waiting first (asc age)
 * AC-inbox-4:  column headers clickable to toggle asc/desc
 * AC-inbox-5:  Kind filter
 * AC-inbox-6:  Domain filter
 * AC-inbox-7:  Status filter (Open default / Decided / All)
 * AC-inbox-8:  Owner/Priority/Impact filter
 * AC-inbox-9:  Row click opens detail drawer
 * AC-inbox-10: QC actions — Approve / Request Changes (with reason)
 * AC-inbox-11: Decision actions — Approve / Reject (with reason)
 * AC-inbox-12: Cascade actions — Approve / Reject / Request Ad-hoc Review
 * AC-inbox-13: Decided rows show NO action controls
 * AC-inbox-14: "Inbox" in Command sidebar (handled by layout.config.ts)
 * AC-inbox-19: cascade visible to any Inbox viewer
 * AC-inbox-20: Action buttons hidden for users lacking required permission
 * AC-inbox-22: empty state when no match
 *
 * PRD §12.3.8
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Inbox, ChevronUp, ChevronDown, ChevronsUpDown, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { t, __resolve } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select/Select';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Alert } from '@/components/ui/Alert';
import { showToast } from '@/components/ui/Toast';
// AC-hard-3: shared ReasonDialog — single source of truth for reason dialogs
import { ReasonDialog } from '@/components/roros/ReasonDialog';
import { LoadingState } from '@/components/ui/LoadingState/LoadingState';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { useRorosInbox } from '@/hooks/roros/inbox';
import {
  useRorosInboxQcTransition,
  useRorosInboxDecisionApprove,
  useRorosInboxDecisionReject,
  useRorosInboxCascadeApprove,
  useRorosInboxCascadeReject,
  useRorosInboxCascadeRequestAdHoc,
} from '@/hooks/roros/inbox';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosActors } from '@/hooks/roros/actors';
import { InboxDrawer } from './InboxDrawer';
import type { InboxItemDTO, InboxItemKind, InboxStatusFilter, InboxSortDir } from '@/types/roros/inbox';

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatAge(ageMs: number): string {
  const seconds = Math.floor(ageMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// KIND_LABELS wrapped in t() so labels are translatable at render time.
function kindLabel(kind: InboxItemKind): string {
  const map: Record<InboxItemKind, string> = {
    qc: t('QC'),
    decision: t('Decision'),
    cascade: t('Cascade'),
  };
  return map[kind] ?? kind;
}

const KIND_BADGE_VARIANTS: Record<InboxItemKind, 'warning' | 'primary' | 'default'> = {
  qc: 'warning',
  decision: 'primary',
  cascade: 'default',
};

const STATE_BADGE_MAP: Record<string, 'success' | 'warning' | 'error' | 'default' | 'primary'> = {
  pending: 'warning',
  pending_approval: 'warning',
  quality_confirmation: 'warning',
  escalated: 'error',
  done: 'default',
  approved: 'success',
  rejected: 'error',
  ad_hoc_review_requested: 'primary',
};

function stateLabel(state: string): string {
  const labels: Record<string, string> = {
    pending: t('Pending'),
    pending_approval: t('Pending Approval'),
    quality_confirmation: t('QC Review'),
    escalated: t('Escalated'),
    done: t('Done'),
    approved: t('Approved'),
    rejected: t('Rejected'),
    ad_hoc_review_requested: t('Ad-hoc Review'),
  };
  return labels[state] ?? state;
}

// ─── State helpers (AC-inbox-13, F2) ────────────────────────────────────────
//
// F2: split the old isDecided() into two helpers:
//   isTerminalState — used for "Decided" label; true only for genuinely terminal states.
//   hasActionableState — used to show/hide action buttons; true only when actions are valid.
//
// BUG-3: QC terminal = released ONLY. Backend QC_OPEN_STATUSES includes done/escalated
// so a 'done' qc row is still open — labeling it "Decided" was wrong.
// Decision: terminal = approved | rejected.
// Cascade: terminal = approved | rejected | ad_hoc_review_requested.
// QC actionable = quality_confirmation only. Decision actionable = pending_approval.
// Cascade actionable = pending.

function isTerminalState(item: InboxItemDTO): boolean {
  if (item.kind === 'qc') return item.state === 'released';
  if (item.kind === 'decision') return ['approved', 'rejected'].includes(item.state);
  if (item.kind === 'cascade') return ['approved', 'rejected', 'ad_hoc_review_requested'].includes(item.state);
  return true;
}

function hasActionableState(item: InboxItemDTO): boolean {
  if (item.kind === 'qc') return item.state === 'quality_confirmation';
  if (item.kind === 'decision') return item.state === 'pending_approval';
  if (item.kind === 'cascade') return item.state === 'pending';
  return false;
}

// ─── Sort icon ─────────────────────────────────────────────────────────────

type SortField = 'kind' | 'title' | 'state' | 'createdAt';

function SortIcon({ field, sortBy, sortDir }: { field: SortField; sortBy: SortField; sortDir: InboxSortDir }) {
  if (field !== sortBy) return <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/50" aria-hidden />;
  return sortDir === 'asc'
    ? <ChevronUp className="h-3.5 w-3.5" aria-hidden />
    : <ChevronDown className="h-3.5 w-3.5" aria-hidden />;
}

// ─── Radix Select sentinel ────────────────────────────────────────────────────

const ALL_SENTINEL = '__all__';

const toSelectValue = (v: string) => (v === '' ? ALL_SENTINEL : v);
const fromSelectValue = (v: string) => (v === ALL_SENTINEL ? '' : v);

// ─── Filter options ──────────────────────────────────────────────────────────

const IMPACT_OPTIONS = [
  { value: ALL_SENTINEL, label: t('All Impacts') },
  { value: 'root', label: t('Root') },
  { value: 'trunk', label: t('Trunk') },
  { value: 'branch', label: t('Branch') },
  { value: 'leaf', label: t('Leaf') },
];

const PRIORITY_OPTIONS = [
  { value: ALL_SENTINEL, label: t('All Priorities') },
  { value: '1', label: t('P1') },
  { value: '2', label: t('P2') },
  { value: '3', label: t('P3') },
  { value: '4', label: t('P4') },
  { value: '5', label: t('P5') },
];

// ─── InboxPage ─────────────────────────────────────────────────────────────

export function InboxPage() {
  // ── Filter state ──────────────────────────────────────────────────────
  const [kindFilter, setKindFilter] = useState<string>('');
  const [domainFilter, setDomainFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<InboxStatusFilter>('open');
  const [impactFilter, setImpactFilter] = useState<string>('');
  const [ownerFilter, setOwnerFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<InboxSortDir>('asc');

  // ── Drawer state ──────────────────────────────────────────────────────
  const [drawerItem, setDrawerItem] = useState<InboxItemDTO | null>(null);

  // ── Data fetching ─────────────────────────────────────────────────────
  const { data, isLoading, error } = useRorosInbox({
    kind: (kindFilter as InboxItemKind) || undefined,
    domainId: domainFilter || undefined,
    status: statusFilter,
    impactClassification: impactFilter || undefined,
    ownerActorId: ownerFilter || undefined,
    priority: priorityFilter ? parseInt(priorityFilter, 10) : undefined,
    sortBy,
    sortDir,
  });

  const { data: domains } = useRorosDomains();
  const { data: actors } = useRorosActors();

  // ── Sort toggle ───────────────────────────────────────────────────────
  const handleSort = useCallback((field: SortField) => {
    if (field === sortBy) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  }, [sortBy]);

  // ── Domain options ─────────────────────────────────────────────────────
  const domainOptions = useMemo(() => {
    const opts = [{ value: ALL_SENTINEL, label: t('All Domains') }];
    if (Array.isArray(domains)) {
      (domains as { id: string; name: string; isActive?: boolean }[])
        .filter(d => d.isActive !== false)
        .forEach(d => opts.push({ value: d.id, label: d.name }));
    }
    return opts;
  }, [domains]);

  // ── Actor lookup ─────────────────────────────────────────────────────
  const actorMap = useMemo(() => {
    const m: Record<string, string> = {};
    if (Array.isArray(actors)) {
      (actors as { id: string; name: string }[]).forEach(a => { m[a.id] = a.name; });
    }
    return m;
  }, [actors]);

  const items = data?.items ?? [];

  return (
    <RorosPage title={t('Inbox')} icon={<Inbox className="h-5 w-5" />}>
      {/* ── Filter toolbar ─────────────────────────────────────────────── */}
      <div
        className="flex flex-wrap gap-3 mb-4"
        data-testid="inbox-filter-toolbar"
      >
        <Select
          aria-label={t('Filter by Type')}
          value={toSelectValue(kindFilter)}
          onValueChange={v => setKindFilter(fromSelectValue(v))}
          options={[
            { value: ALL_SENTINEL, label: t('All Types') },
            { value: 'qc', label: t('QC') },
            { value: 'decision', label: t('Decision') },
            { value: 'cascade', label: t('Cascade') },
          ]}
          wrapperClassName="w-36 shrink-0"
          data-testid="inbox-filter-kind"
        />
        <Select
          aria-label={t('Filter by Domain')}
          value={toSelectValue(domainFilter)}
          onValueChange={v => setDomainFilter(fromSelectValue(v))}
          options={domainOptions}
          wrapperClassName="w-44 shrink-0"
          data-testid="inbox-filter-domain"
        />
        <Select
          aria-label={t('Filter by Status')}
          value={statusFilter}
          onValueChange={v => setStatusFilter(v as InboxStatusFilter)}
          options={[
            { value: 'all', label: t('All') },
            { value: 'open', label: t('Open') },
            { value: 'decided', label: t('Decided') },
            { value: 'quality_confirmation', label: t('Quality Confirmation') },
            { value: 'escalated', label: t('Escalated') },
            { value: 'done', label: t('Done') },
            { value: 'pending_approval', label: t('Pending Approval') },
            { value: 'approved', label: t('Approved') },
            { value: 'rejected', label: t('Rejected') },
            { value: 'pending', label: t('Pending') },
            { value: 'ad_hoc_review_requested', label: t('Ad-hoc Review Requested') },
          ]}
          wrapperClassName="w-52 shrink-0"
          data-testid="inbox-filter-status"
        />
        <Select
          aria-label={t('Filter by Impact')}
          value={toSelectValue(impactFilter)}
          onValueChange={v => setImpactFilter(fromSelectValue(v))}
          options={IMPACT_OPTIONS}
          wrapperClassName="w-40 shrink-0"
          data-testid="inbox-filter-impact"
        />
        <Select
          aria-label={t('Filter by Owner')}
          value={toSelectValue(ownerFilter)}
          onValueChange={v => setOwnerFilter(fromSelectValue(v))}
          options={[
            { value: ALL_SENTINEL, label: t('All Owners') },
            ...Array.isArray(actors)
              ? (actors as { id: string; name: string }[]).map(a => ({ value: a.id, label: a.name }))
              : [],
          ]}
          wrapperClassName="w-40 shrink-0"
          data-testid="inbox-filter-owner"
        />
        <Select
          aria-label={t('Filter by Priority')}
          value={toSelectValue(priorityFilter)}
          onValueChange={v => setPriorityFilter(fromSelectValue(v))}
          options={PRIORITY_OPTIONS}
          wrapperClassName="w-36 shrink-0"
          data-testid="inbox-filter-priority"
        />
      </div>

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {error && (
        <Alert variant="error" className="mb-4">
          {t('Failed to load inbox. Please refresh.')}
        </Alert>
      )}

      {/* ── Loading ────────────────────────────────────────────────────── */}
      {isLoading && <LoadingState text={t('Loading inbox…')} />}

      {/* ── Empty state (AC-inbox-22) ──────────────────────────────────── */}
      {!isLoading && !error && items.length === 0 && (
        <div data-testid="inbox-empty-state">
          <EmptyState
            icon={<Inbox className="h-10 w-10 text-muted-foreground" />}
            title={t('All caught up')}
            description={t('No open items match the active filters.')}
          />
        </div>
      )}

      {/* ── Table (AC-inbox-1, AC-inbox-2) ────────────────────────────── */}
      {!isLoading && !error && items.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden" data-testid="inbox-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left font-medium cursor-pointer select-none"
                  onClick={() => handleSort('kind')}
                  data-testid="inbox-col-kind"
                >
                  <span className="flex items-center gap-1">
                    {t('Type')}
                    <SortIcon field="kind" sortBy={sortBy} sortDir={sortDir} />
                  </span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left font-medium cursor-pointer select-none"
                  onClick={() => handleSort('title')}
                  data-testid="inbox-col-title"
                >
                  <span className="flex items-center gap-1">
                    {t('Title / Summary')}
                    <SortIcon field="title" sortBy={sortBy} sortDir={sortDir} />
                  </span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left font-medium cursor-pointer select-none"
                  onClick={() => handleSort('state')}
                  data-testid="inbox-col-state"
                >
                  <span className="flex items-center gap-1">
                    {t('State')}
                    <SortIcon field="state" sortBy={sortBy} sortDir={sortDir} />
                  </span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left font-medium cursor-pointer select-none"
                  onClick={() => handleSort('createdAt')}
                  data-testid="inbox-col-age"
                >
                  <span className="flex items-center gap-1">
                    {t('Age')}
                    <SortIcon field="createdAt" sortBy={sortBy} sortDir={sortDir} />
                  </span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left font-medium"
                  data-testid="inbox-col-owner-domain"
                >
                  {t('Owner / Domain')}
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left font-medium"
                  data-testid="inbox-col-actions"
                >
                  {t('Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <InboxRow
                  key={item.id}
                  item={item}
                  actorMap={actorMap}
                  domainMap={
                    Array.isArray(domains)
                      ? Object.fromEntries((domains as { id: string; name: string }[]).map(d => [d.id, d.name]))
                      : {}
                  }
                  isEven={idx % 2 === 0}
                  onClick={() => setDrawerItem(item)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Detail Drawer (AC-inbox-9) ────────────────────────────────── */}
      {drawerItem && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/30"
            onClick={() => setDrawerItem(null)}
            aria-hidden="true"
          />
          <InboxDrawer
            item={drawerItem}
            onClose={() => setDrawerItem(null)}
            onActionDone={() => setDrawerItem(null)}
          />
        </>
      )}
    </RorosPage>
  );
}

// ─── InboxRow ──────────────────────────────────────────────────────────────

interface InboxRowProps {
  item: InboxItemDTO;
  actorMap: Record<string, string>;
  domainMap: Record<string, string>;
  isEven: boolean;
  onClick: () => void;
}

function InboxRow({ item, actorMap, domainMap, isEven, onClick }: InboxRowProps) {
  const ownerName = item.ownerActorId ? (actorMap[item.ownerActorId] ?? item.ownerActorId.slice(0, 8)) : '—';
  const domainName = item.domainId ? (domainMap[item.domainId] ?? item.domainId.slice(0, 8)) : '—';
  // F2: use isTerminalState for the "Decided" label; hasActionableState for buttons
  const terminal = isTerminalState(item);
  const actionable = hasActionableState(item);

  return (
    <tr
      onClick={onClick}
      className={[
        'cursor-pointer border-b border-border last:border-0 transition-colors',
        isEven ? 'bg-background' : 'bg-muted/10',
        'hover:bg-muted/30',
      ].join(' ')}
      data-testid={`inbox-row-${item.kind}`}
      data-id={item.id}
      data-decided={terminal ? 'true' : 'false'}
    >
      <td className="px-3 py-2.5">
        <Badge variant={KIND_BADGE_VARIANTS[item.kind]}>
          {kindLabel(item.kind)}
        </Badge>
      </td>
      <td className="px-3 py-2.5 max-w-xs truncate font-medium">{item.title}</td>
      <td className="px-3 py-2.5">
        <Badge variant={STATE_BADGE_MAP[item.state] ?? 'default'}>
          {stateLabel(item.state)}
        </Badge>
      </td>
      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
        {formatAge(item.ageMs)}
      </td>
      <td className="px-3 py-2.5 text-muted-foreground text-xs">
        <div>{ownerName}</div>
        <div>{domainName}</div>
      </td>
      {/* BUGFIX (bug board): only swallow the row click when this cell renders interactive
          action buttons (and their dialogs). For non-actionable rows the cell shows plain
          "Details →" / "Decided" text, so the click must bubble to the row and open the
          detail drawer — matching every other column. Previously the blanket
          stopPropagation made the Details/Actions cell the one dead spot in the row. */}
      <td
        className="px-3 py-2.5"
        onClick={actionable ? (e) => e.stopPropagation() : undefined}
      >
        {/* AC-inbox-10/11/12/13/20: inline actions; show "Decided" only for genuinely terminal rows (F2) */}
        {actionable
          ? <InboxRowActions item={item} />
          : terminal
            ? <span className="text-xs text-muted-foreground italic" data-testid="row-no-actions">{t('Decided')}</span>
            : <span className="text-xs text-muted-foreground">{t('Details →')}</span>
        }
      </td>
    </tr>
  );
}

// ─── InboxRowActions ───────────────────────────────────────────────────────

/**
 * Inline action buttons per kind.
 * AC-inbox-20: hidden when user lacks the required permission.
 * AC-inbox-13: parent already guards decided state; this always renders actions.
 */
function InboxRowActions({ item }: { item: InboxItemDTO }) {
  const { hasPermission } = usePermissions();

  if (item.kind === 'qc') {
    const canManage = hasPermission(PERMISSIONS.ROROS_CONTROL_TOWER_MANAGE);
    if (!canManage) return <span className="text-xs text-muted-foreground">{t('Details →')}</span>;
    return <QcRowActions item={item} />;
  }

  if (item.kind === 'decision') {
    const canApprove = hasPermission(PERMISSIONS.ROROS_DECISIONS_APPROVE);
    if (!canApprove) return <span className="text-xs text-muted-foreground">{t('Details →')}</span>;
    return <DecisionRowActions item={item} />;
  }

  if (item.kind === 'cascade') {
    const canDecide = hasPermission(PERMISSIONS.ROROS_VERIFICATION_INBOX_DECIDE);
    if (!canDecide) return <span className="text-xs text-muted-foreground">{t('Details →')}</span>;
    return <CascadeRowActions item={item} />;
  }

  return null;
}

// ─── QcRowActions ──────────────────────────────────────────────────────────

function QcRowActions({ item }: { item: InboxItemDTO }) {
  const qcTransition = useRorosInboxQcTransition();
  const [approveOpen, setApproveOpen] = useState(false);
  const [requestChangesOpen, setRequestChangesOpen] = useState(false);

  const handleApprove = () => {
    qcTransition.mutate(
      { sourceId: item.sourceId, toStatus: 'released', reason: t('Approved from Inbox') },
      {
        onSuccess: () => showToast.success(t('Approved — work item released.')),
        // F4: use friendly error formatter for ROROS_QC_REQUIRED / ROROS_PARENT_CHILDREN_INCOMPLETE
        onError: (err: any) => showToast.error(formatRowError(err, t('Failed to approve.'))),
      },
    );
  };

  const handleRequestChanges = async (reason: string) => {
    try {
      await qcTransition.mutateAsync({ sourceId: item.sourceId, toStatus: 'in_progress', reason });
      showToast.success(t('Changes requested — sent back with your feedback.'));
    } catch (err: any) {
      const friendly = formatRowError(err, t('Failed to request changes.'));
      showToast.error(friendly);
      throw new Error(friendly);
    }
  };

  return (
    <>
      <div className="flex gap-1.5" data-testid="qc-row-actions">
        <Button
          size="sm"
          variant="success"
          disabled={qcTransition.isPending}
          onClick={() => setApproveOpen(true)}
          data-testid="row-approve-qc-btn"
        >
          <CheckCircle className="h-3.5 w-3.5 mr-1" aria-hidden />
          {t('Approve')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={qcTransition.isPending}
          onClick={() => setRequestChangesOpen(true)}
          data-testid="row-request-changes-btn"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1" aria-hidden />
          {t('Changes')}
        </Button>
      </div>
      <ConfirmDialog
        isOpen={approveOpen}
        onClose={() => setApproveOpen(false)}
        title={t('Approve work item?')}
        description={`${t('This will mark')} "${item.title}" ${t('as released.')}`}
        confirmText={t('Approve')}
        onConfirm={() => { setApproveOpen(false); handleApprove(); }}
      />
      <RowReasonDialog
        isOpen={requestChangesOpen}
        title={t('Request Changes')}
        onClose={() => setRequestChangesOpen(false)}
        onSubmit={handleRequestChanges}
        isPending={qcTransition.isPending}
      />
    </>
  );
}

// ─── DecisionRowActions ────────────────────────────────────────────────────

function DecisionRowActions({ item }: { item: InboxItemDTO }) {
  const approveMutation = useRorosInboxDecisionApprove();
  const rejectMutation = useRorosInboxDecisionReject();
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const anyPending = approveMutation.isPending || rejectMutation.isPending;

  const handleApprove = () => {
    approveMutation.mutate(
      { sourceId: item.sourceId },
      {
        onSuccess: () => showToast.success(t('Decision approved.')),
        onError: (err: any) => showToast.error(err?.message ?? t('Failed to approve.')),
      },
    );
  };

  const handleReject = async (reason: string) => {
    try {
      await rejectMutation.mutateAsync({ sourceId: item.sourceId, reason });
      showToast.success(t('Decision rejected.'));
    } catch (err: any) {
      const friendly = formatRowError(err, t('Failed to reject decision.'));
      showToast.error(friendly);
      throw new Error(friendly);
    }
  };

  return (
    <>
      <div className="flex gap-1.5" data-testid="decision-row-actions">
        <Button
          size="sm"
          variant="success"
          disabled={anyPending}
          onClick={() => setApproveOpen(true)}
          data-testid="row-approve-decision-btn"
        >
          <CheckCircle className="h-3.5 w-3.5 mr-1" aria-hidden />
          {t('Approve')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={anyPending}
          onClick={() => setRejectOpen(true)}
          data-testid="row-reject-decision-btn"
        >
          <XCircle className="h-3.5 w-3.5 mr-1" aria-hidden />
          {t('Reject')}
        </Button>
      </div>
      <ConfirmDialog
        isOpen={approveOpen}
        onClose={() => setApproveOpen(false)}
        title={t('Approve decision?')}
        description={`${t('This will approve')} "${item.title}".`}
        confirmText={t('Approve')}
        onConfirm={() => { setApproveOpen(false); handleApprove(); }}
      />
      <RowReasonDialog
        isOpen={rejectOpen}
        title={t('Reject Decision')}
        onClose={() => setRejectOpen(false)}
        onSubmit={handleReject}
        isPending={rejectMutation.isPending}
      />
    </>
  );
}

// ─── CascadeRowActions ─────────────────────────────────────────────────────

function CascadeRowActions({ item }: { item: InboxItemDTO }) {
  const approveMutation = useRorosInboxCascadeApprove();
  const rejectMutation = useRorosInboxCascadeReject();
  const adHocMutation = useRorosInboxCascadeRequestAdHoc();
  const anyPending = approveMutation.isPending || rejectMutation.isPending || adHocMutation.isPending;
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const handleApprove = () => {
    approveMutation.mutate(
      { sourceId: item.sourceId },
      {
        onSuccess: () => showToast.success(t('Approved successfully.')),
        onError: (err: any) => showToast.error(err?.message ?? t('Action failed.')),
      },
    );
  };

  const handleReject = async (reason: string) => {
    try {
      await rejectMutation.mutateAsync({ sourceId: item.sourceId, reason });
      showToast.success(t('Rejected successfully.'));
    } catch (err: any) {
      const friendly = formatRowError(err, t('Failed to reject.'));
      showToast.error(friendly);
      throw new Error(friendly);
    }
  };

  const handleAdHoc = () => {
    adHocMutation.mutate(
      { sourceId: item.sourceId },
      {
        onSuccess: () => showToast.success(t('Ad-hoc review requested.')),
        onError: (err: any) => showToast.error(err?.message ?? t('Action failed.')),
      },
    );
  };

  return (
    <>
      <div className="flex gap-1.5 flex-wrap" data-testid="cascade-row-actions">
        <Button
          size="sm"
          variant="success"
          disabled={anyPending}
          onClick={() => setApproveOpen(true)}
          data-testid="row-approve-cascade-btn"
        >
          <CheckCircle className="h-3.5 w-3.5 mr-1" aria-hidden />
          {t('Approve')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={anyPending}
          onClick={handleAdHoc}
          isLoading={adHocMutation.isPending}
          data-testid="row-adhoc-cascade-btn"
        >
          {t('Ad-hoc')}
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={anyPending}
          onClick={() => setRejectOpen(true)}
          data-testid="row-reject-cascade-btn"
        >
          <XCircle className="h-3.5 w-3.5 mr-1" aria-hidden />
          {t('Reject')}
        </Button>
      </div>
      <ConfirmDialog
        isOpen={approveOpen}
        onClose={() => setApproveOpen(false)}
        title={t('Approve cascade submission?')}
        description={`${t('This will approve')} "${item.title}".`}
        confirmText={t('Approve')}
        onConfirm={() => { setApproveOpen(false); handleApprove(); }}
      />
      <RowReasonDialog
        isOpen={rejectOpen}
        title={t('Reject Cascade Submission')}
        onClose={() => setRejectOpen(false)}
        onSubmit={handleReject}
        isPending={rejectMutation.isPending}
      />
    </>
  );
}

// ─── Shared row error helper ───────────────────────────────────────────────
//
// F3: wrap WIP strings via __resolve so they pass translations:check.
// F3: add the missing per_actor branch (parity with InboxDrawer.formatTransitionErrorMessage).
// F4: friendly messages for ROROS_QC_REQUIRED and ROROS_PARENT_CHILDREN_INCOMPLETE.

function formatRowError(err: any, fallback: string): string {
  const code: string = err?.code ?? '';
  const details = err?.details ?? {};

  if (code === 'ROROS_WIP_LIMIT_REACHED') {
    const limit = typeof details?.limit === 'number' ? details.limit : null;
    const limitType: string = details?.limitType ?? '';
    if (limitType === 'per_actor' && limit !== null) {
      return __resolve(
        'ui::Cannot submit: this actor already has {{limit}} in-progress items. Resolve some before assigning more.',
        { limit },
      );
    }
    if (limit !== null) {
      return __resolve(
        'ui::Cannot submit: WIP limit reached ({{limit}} in-progress). Resolve some items before assigning more.',
        { limit },
      );
    }
    return t('Cannot submit: WIP limit reached. Resolve some in-progress items before assigning more.');
  }

  if (code === 'ROROS_INVALID_STATUS_TRANSITION') {
    return t('This status transition is not allowed from the current state.');
  }

  // F4: friendly QC-approve errors
  if (code === 'ROROS_QC_REQUIRED') {
    return t('Cannot approve: the AI Self-QC report has not passed. Review the QC criteria and try again.');
  }
  if (code === 'ROROS_PARENT_CHILDREN_INCOMPLETE') {
    const n = typeof details?.activeChildren === 'number' ? details.activeChildren : null;
    if (n !== null) return __resolve('ui::Cannot approve: {{n}} sub-item(s) still active. Complete them first.', { n });
    return t('Cannot approve: some sub-items are still active. Complete them first.');
  }

  return err?.message || fallback;
}

// AC-hard-3: RowReasonDialog is now the shared ReasonDialog from @/components/roros/ReasonDialog
// Alias kept for backward compat with existing JSX usage in this file.
const RowReasonDialog = ReasonDialog;

